// src/lib/janetQueueFlush.js
//
// Drain-time dispatcher for 'janet_queue_decide' mutations. Mirrors the
// pattern in sendEmailFlush.js — at flush time we fetch a fresh auth
// session (stored tokens may have expired), POST the queue decision
// payload to /api/janet-queue-decide, and translate response codes into
// retriable / permanent semantics for mutationQueue's backoff loop.
//
// 4xx responses (queue row already approved/rejected, validation, auth
// gone) throw with HTTP-XXX prefixes so mutationQueue marks them as
// permanent failures rather than retrying forever.

import { supabase } from './supabase'
import { getSessionWithTimeout } from './authSession'

export async function janetQueueDecideFlush(args) {
  const sessionResult = await getSessionWithTimeout(8000)
  if (sessionResult.timedOut) {
    // Transient — retry on next drain
    throw new Error('HTTP 408 — session lookup timed out')
  }
  const token = sessionResult.data?.session?.access_token
  if (!token) throw new Error('HTTP 401 — not signed in')

  const resp = await fetch('/api/janet-queue-decide', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(args || {}),
  })

  if (!resp.ok) {
    let errMsg = `HTTP ${resp.status}`
    try {
      const errData = await resp.json()
      if (errData?.error) errMsg = `HTTP ${resp.status} — ${errData.error}`
    } catch { /* non-JSON response */ }
    throw new Error(errMsg)
  }

  return resp.json().catch(() => ({}))
}

// Enqueue a queue-decide action for offline/failed-network execution.
// Caller handles the immediate toast — this just persists the payload.
export async function enqueueJanetQueueDecide(args) {
  const { enqueue } = await import('./mutationQueue')
  return enqueue('janet_queue_decide', args)
}
